import { useState, useEffect } from "react";
import { Mail, ArrowLeft, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { useNotification } from "../context/NotificationContext";

import { useAuth } from "../services/AuthService";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [countdown, setCountdown] = useState(0);
  const { forgotPassword, verifyOtp, loading, clearError } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      return;
    }

    try {
      const result = await forgotPassword(email);
      if (result.status === "success") {
        setStep(2);
        setCountdown(60);
        showNotification({
          type: "success",
          title: "OTP Sent",
          message: "A verification code has been sent to your email."
        });
      } else {
        showNotification({
          type: "failed",
          title: "Failed",
          message: result.message || "Failed to send OTP."
        });
      }
    } catch (err) {
      showNotification({
        type: "failed",
        title: "Error",
        message: err.response?.data?.message || "An unexpected error occurred."
      });
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      return;
    }

    try {
      const result = await verifyOtp(email, otp);
      if (result.status === "success") {
        showNotification({
          type: "success",
          title: "Verified",
          message: "Code verified. Please set your new password."
        });
        // Use the OTP as the token for the reset page
        navigate(`/reset-password?email=${encodeURIComponent(email)}&token=${otp}`);
      } else {
        showNotification({
          type: "failed",
          title: "Verification Failed",
          message: result.message || "Invalid or expired code."
        });
      }
    } catch (err) {
      showNotification({
        type: "failed",
        title: "Error",
        message: err.response?.data?.message || "An unexpected error occurred."
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO title="Forgot Password" url="/forgot-password" />
      <JazzHeader />

      <main className="flex-1 flex items-center justify-center p-4 py-12 md:py-20 bg-[#fafafa] relative overflow-hidden">
        {/* Subtle background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-jazz-brown/5 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-jazz-brown/5 rounded-full blur-3xl"></div>
        </div>

        <div className="w-full max-w-md relative animate-fade-in">
          {/* Back Link */}
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-black/60 hover:text-jazz-brown mb-8 font-bold text-[10px] tracking-[2px] uppercase transition-all group"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Login</span>
          </Link>

          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-white border border-black/5 flex items-center justify-center shadow-sm mx-auto mb-6 rounded-2xl">
              <img src="/static/images/temb_music_logo.png" alt="Temidayo Balogun Logo" className="w-12 h-12 object-contain" />
            </div>
            <h1 className="text-4xl font-serif italic text-black mb-2">
              {step === 1 ? "Forgot Password?" : "Verify OTP"}
            </h1>
            <p className="text-xs font-bold tracking-[1px] uppercase text-black/60">
              {step === 1
                ? "Enter your email for a reset code"
                : "A 6-digit code has been sent to your email"}
            </p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-[2rem] shadow-xl border border-black/5 p-8 md:p-10">
            {step === 1 ? (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-[10px] font-bold tracking-[2px] uppercase text-black/60 ml-1">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40 group-focus-within:text-jazz-brown transition-colors" size={18} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-[#fafafa] border border-black/5 rounded-xl text-sm font-semibold text-black placeholder-black/40 focus:outline-none focus:border-jazz-brown transition-all"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-5 px-6 bg-black text-white font-bold text-[10px] tracking-[3px] uppercase rounded-xl shadow-lg hover:bg-jazz-brown transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Send OTP <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-100 rounded-xl p-4 animate-fade-in flex items-start gap-3">
                  <CheckCircle className="text-green-600 mt-0.5 shrink-0" size={18} />
                  <div>
                    <h3 className="text-green-800 font-bold text-[10px] uppercase tracking-wider">OTP Sent Successfully</h3>
                    <p className="text-green-700 text-xs mt-1">
                      Check your inbox at <span className="font-semibold">{email}</span>
                    </p>
                  </div>
                </div>

                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-bold tracking-[2px] uppercase text-black/60 text-center">
                      Enter 6-Digit Code
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="w-full px-4 py-4 bg-[#fafafa] border border-black/5 rounded-xl text-black text-center text-3xl font-mono tracking-[0.5em] placeholder-black/10 focus:outline-none focus:border-jazz-brown transition-all"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                  </div>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleSendOTP}
                      disabled={countdown > 0 || loading}
                      className="text-[10px] font-bold tracking-[1px] uppercase text-jazz-brown hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      {countdown > 0 ? `Resend code in ${countdown}s` : "Resend code"}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full py-5 px-6 bg-black text-white font-bold text-[10px] tracking-[3px] uppercase rounded-xl shadow-lg hover:bg-jazz-brown transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-3"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify Code"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(""); clearError(); }}
                    className="w-full text-black/60 text-[10px] font-bold tracking-[1px] uppercase hover:text-black transition-colors"
                  >
                    Use a different email address
                  </button>
                </form>
              </div>
            )}
          </div>

          <p className="text-center text-[9px] font-bold tracking-[2px] uppercase text-black/40 mt-12 leading-relaxed">
            &copy; {new Date().getFullYear()} TEMB Music. All rights reserved.
          </p>
        </div>
      </main>

      <JazzFooter />

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
      `}</style>
    </div>
  );
}
